import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Check, Crown, MessageCircle, Sparkles, Zap, Building2 } from "lucide-react";
import { carregarPlanoAtual } from "../lib/planos";

const API = `${window.location.origin}/api`;

const ICONES = {
  start: Sparkles,
  pro: Zap,
  premium: Crown,
  corporativo: Building2,
};

const CORES = {
  start: {
    badge: "bg-slate-100 text-slate-700",
    card: "border-slate-200 hover:border-slate-400",
    btn: "bg-slate-800 hover:bg-slate-900 text-white",
    accent: "text-slate-700",
  },
  pro: {
    badge: "bg-blue-100 text-blue-700",
    card: "border-blue-200 hover:border-blue-400 ring-2 ring-blue-100",
    btn: "bg-blue-600 hover:bg-blue-700 text-white",
    accent: "text-blue-700",
  },
  premium: {
    badge: "bg-amber-100 text-amber-700",
    card: "border-amber-300 hover:border-amber-500 ring-2 ring-amber-100",
    btn: "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white",
    accent: "text-amber-700",
  },
  corporativo: {
    badge: "bg-purple-100 text-purple-700",
    card: "border-purple-200 hover:border-purple-400",
    btn: "bg-purple-600 hover:bg-purple-700 text-white",
    accent: "text-purple-700",
  },
};

function formatPreco(v) {
  if (v == null) return "Sob consulta";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function PlanosPage() {
  const [catalogo, setCatalogo] = useState([]);
  const [planoAtualId, setPlanoAtualId] = useState(null);
  const [trialInfo, setTrialInfo] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acaoPlano, setAcaoPlano] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cat, meu] = await Promise.all([
          axios.get(`${API}/planos/catalogo`),
          carregarPlanoAtual(true).catch(() => null),
        ]);
        setCatalogo(cat.data);
        setPlanoAtualId(meu?.plano?.id || null);
        setTrialInfo(meu?.trial || null);
        setSubStatus(meu?.subscription_status || null);
      } catch (e) {
        toast.error("Erro ao carregar planos");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const escolherPlano = async (plano) => {
    if (plano.id === "corporativo") {
      const url = `https://wa.me/5567991992593?text=${encodeURIComponent(
        "Olá! Gostaria de conhecer o plano Corporativo."
      )}`;
      window.open(url, "_blank");
      return;
    }
    setAcaoPlano(plano.id);
    try {
      const { data } = await axios.post(`${API}/planos/checkout`, { plano: plano.id });
      if (data.status === "checkout_criado" && data.init_point) {
        // Redireciona pro Mercado Pago
        window.location.href = data.init_point;
        return;
      }
      if (data.status === "em_breve" || data.status === "erro_mp") {
        toast.info(data.message, {
          duration: 8000,
          action: {
            label: "WhatsApp",
            onClick: () => window.open(data.whatsapp, "_blank"),
          },
        });
      } else if (data.status === "contato_comercial") {
        window.open(data.whatsapp, "_blank");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao iniciar checkout");
    } finally {
      setAcaoPlano(null);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Carregando planos…</div>;
  }

  return (
    <div className="space-y-8" data-testid="planos-page">
      {/* Hero */}
      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-100 text-amber-800 rounded-full text-xs font-bold tracking-wider uppercase mb-4">
          <Crown className="h-3.5 w-3.5" /> Planos de assinatura
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold" style={{ fontFamily: "Work Sans" }}>
          Escolha o plano ideal para sua operação
        </h1>
        <p className="mt-3 text-gray-600">
          De autônomos a operações com dezenas de carros — cresça sem trocar de sistema.
        </p>
        {/* Banner de trial / status */}
        {trialInfo?.ativo && (
          <div className="mt-5 inline-flex items-center gap-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-xl px-4 py-3 text-sm shadow-sm" data-testid="trial-banner">
            <span className="text-2xl">⏱️</span>
            <div className="text-left">
              <p className="font-bold text-amber-900">
                Você está no trial de {trialInfo.dias_restantes} dia(s)
              </p>
              <p className="text-xs text-amber-800">
                Plano <strong className="uppercase">{trialInfo.plano_escolhido}</strong> ativo até {new Date(trialInfo.termina_em).toLocaleDateString("pt-BR")}. Assine para manter os recursos.
              </p>
            </div>
          </div>
        )}
        {!trialInfo?.ativo && subStatus === "trial" && (
          <div className="mt-5 inline-flex items-center gap-3 bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-sm shadow-sm" data-testid="trial-expirado-banner">
            <span className="text-2xl">⛔</span>
            <div className="text-left">
              <p className="font-bold text-red-900">Seu trial terminou</p>
              <p className="text-xs text-red-800">
                Você está no Start. Reative seu plano para liberar tudo novamente.
              </p>
            </div>
          </div>
        )}
        {subStatus === "authorized" && (
          <div className="mt-5 inline-flex items-center gap-3 bg-emerald-50 border border-emerald-300 rounded-xl px-4 py-3 text-sm shadow-sm" data-testid="sub-ativa-banner">
            <span className="text-2xl">✅</span>
            <div className="text-left">
              <p className="font-bold text-emerald-900">Assinatura ativa</p>
              <p className="text-xs text-emerald-800">Obrigado por confiar no Compromisso Digital!</p>
            </div>
          </div>
        )}
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {catalogo.map((p) => {
          const Icon = ICONES[p.id] || Sparkles;
          const cores = CORES[p.id] || CORES.start;
          // "Plano atual" só é o plano ATIVAMENTE PAGO — se está em trial,
          // o botão Assinar ainda deve estar clicável para finalizar o pagamento
          // no Mercado Pago mesmo antes do trial acabar.
          const jaAssinado = subStatus === "authorized" || subStatus === "active";
          const atual = jaAssinado && planoAtualId === p.id;
          const noTrialDesteplano = trialInfo?.ativo && trialInfo?.plano_escolhido === p.id;
          const destaque = p.id === "pro";
          return (
            <Card
              key={p.id}
              className={`relative overflow-hidden transition-all ${cores.card} ${
                destaque ? "scale-[1.02] shadow-xl" : "shadow-sm"
              } ${atual ? "ring-2 ring-emerald-400" : ""}`}
              data-testid={`plano-card-${p.id}`}
            >
              {destaque && (
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl">
                  MAIS POPULAR
                </div>
              )}
              {atual && (
                <div className="absolute top-0 left-0 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1 rounded-br-xl">
                  SEU PLANO
                </div>
              )}
              <CardHeader className="pt-8 pb-2">
                <div
                  className={`w-12 h-12 rounded-xl ${cores.badge} flex items-center justify-center mb-2`}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <CardTitle className={`text-2xl ${cores.accent}`}>{p.nome}</CardTitle>
                <p className="text-xs text-gray-500">{p.descricao}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-3xl font-extrabold">{formatPreco(p.preco)}</p>
                  {p.preco != null && (
                    <p className="text-xs text-gray-500">por mês</p>
                  )}
                </div>
                <div className="space-y-1.5 min-h-[180px]">
                  {p.destaques.map((d, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      <span className="text-gray-700">{d}</span>
                    </div>
                  ))}
                </div>
                <Button
                  className={`w-full gap-2 ${cores.btn}`}
                  disabled={atual || acaoPlano === p.id}
                  onClick={() => escolherPlano(p)}
                  data-testid={`btn-assinar-${p.id}`}
                >
                  {atual ? (
                    "Plano atual"
                  ) : p.id === "corporativo" ? (
                    <>
                      <MessageCircle className="h-4 w-4" /> Falar com especialista
                    </>
                  ) : acaoPlano === p.id ? (
                    "Aguarde…"
                  ) : noTrialDesteplano ? (
                    <>Assinar agora e manter acesso <Check className="h-4 w-4" /></>
                  ) : (
                    "Assinar"
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Comparativo simples */}
      <Card className="border-gray-200">
        <CardContent className="p-6">
          <h2 className="text-lg font-bold mb-4">Comparativo rápido</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-gray-500">
                  <th className="py-2">Recurso</th>
                  <th className="py-2 text-center">Start</th>
                  <th className="py-2 text-center">Pro</th>
                  <th className="py-2 text-center">Premium</th>
                  <th className="py-2 text-center">Corp.</th>
                </tr>
              </thead>
              <tbody className="text-gray-800">
                <Row label="Carros/Rotas" v={["1", "5", "10", "Ilimitado"]} />
                <Row label="WhatsApp completo" v={["✓", "✓", "✓", "✓"]} />
                <Row label="Caixa do Carro" v={["✓", "✓", "✓", "✓"]} />
                <Row label="Catálogo + Estoque" v={["✓", "✓", "✓", "✓"]} />
                <Row label="Loja Digital + App próprio no celular" v={["✓", "✓", "✓", "✓"]} />
                <Row label="Acesso de Secretário" v={["—", "✓", "✓", "✓"]} />
                <Row label="Pós-Venda automatizado" v={["—", "✓", "✓", "✓"]} />
                <Row label="Loja Online + InfinitePay" v={["—", "✓", "✓", "✓"]} />
                <Row label="Contas a Pagar PF/PJ" v={["—", "—", "✓", "✓"]} />
                <Row label="Suporte prioritário" v={["—", "—", "✓", "✓"]} />
                <Row label="Onboarding dedicado" v={["—", "—", "—", "✓"]} />
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <p className="text-center text-xs text-gray-500 mt-2">
        Pagamento seguro via Mercado Pago. Cobrança automática mensal — cancele quando quiser.
      </p>
    </div>
  );
}

function Row({ label, v }) {
  return (
    <tr className="border-b last:border-0">
      <td className="py-2.5 font-medium">{label}</td>
      {v.map((x, i) => (
        <td
          key={i}
          className={`py-2.5 text-center ${
            x === "—" ? "text-gray-300" : "text-emerald-700 font-bold"
          }`}
        >
          {x}
        </td>
      ))}
    </tr>
  );
}
